'use client';
import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';

import { TabBar } from '../tab-bar';
import { Header } from '../header';
import Footer from '../footer/footer';
import { CrowdLevelProvider } from '@/context/CrowdLevelContext';
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import {
  useGetLibraryConstantsDictionaryData,
  useIsMPLivliFamily,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import {
  calculateHreflangTags,
  HreflangTag,
} from '@/helpers/rendered-page-creation/calculate-hreflang-tags';
import { PreviewBanner } from '../ui/preview-banner';
import { useUser } from '@/providers/UserProvider';
import { isBrowser } from '@/helpers/is-browser';
import {
  getProfileDetails,
  initiateLogin,
  logOutUser,
} from '@/helpers/api/user-profile-api';
import {
  checkIfValidUser,
  clearUrlParams,
  getLocalStorageItem,
} from '@/helpers/utils';
import Spinner from '../ui/spinner/spinner';
import PWABanner from '../pwa-banner/pwa-banner';
import applyGoogleTranslateDOMPatch from '@/helpers/applyGoogleTranslateDOMPatch';
import useGetMobileAppWebViewHeader from '@/hooks/use-get-mobile-app-web-view';
import { clearIOSPWAFirstRunAuth } from '@/helpers/pwa/clear-ios-pwa-first-run';

const Whatsapp = dynamic(() => import('../whatsapp'), {
  ssr: false,
});
const DigitalConcierge = dynamic(
  () => import('../digital-concierge/digital-concierge'),
  {
    ssr: false,
  },
);

type LayoutProps = {
  meetingPlacePageHome: {
    whatsappNumber: number;
    hideWhatsapp?: boolean;
    meetingPlace?: any;
    menuItem: any;
    menuCards: any[];
    searchResults?: any;
  };
};

type AdditionalProps = { pageContext: any };

export const Layout = ({
  children,
  layoutData,
  footerData,
  pageContext,
  languages,
  node_locale,
  preview,
  hideHeaderFooter,
  isLoggedInServer = false,
}: any) => {
  const isLivliFamily = useIsMPLivliFamily();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const config = useTenantConfig();
  const [hrefLangTags, setHrefLangTags] = useState<HreflangTag[]>([]);
  const [isNotFoundPage, setIsNotFoundPage] = useState(false);
  const childrenRef = React.useRef<HTMLDivElement>(null);
  const dictionary = useGetLibraryConstantsDictionaryData();

  const loginEnabled = config?.NEXT_PUBLIC_ICIM_LOGIN_ENABLED === 'true';
  const tempLoginEnabled = config?.NEXT_PUBLIC_TEMP_LOGIN_ENABLED === 'true';
  // Check if we're on a not-found page by checking for data-not-found attribute
  useEffect(() => {
    const checkNotFound = () => {
      // Check in the entire document
      const notFoundElement = document.querySelector('[data-not-found="true"]');
      setIsNotFoundPage(!!notFoundElement);
    };

    // Check immediately
    checkNotFound();

    // Use MutationObserver to watch for changes
    const observer = new MutationObserver(checkNotFound);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also check after a short delay as a fallback
    const timeout = setTimeout(checkNotFound, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, []);

  // Also respect the hideHeaderFooter prop if passed
  const shouldHideHeaderFooter = hideHeaderFooter || isNotFoundPage;

  useEffect(() => {
    async function fetchTags() {
      const data = await calculateHreflangTags(
        params,
        languages,
        config,
        preview,
      );
      setHrefLangTags(data);
    }
    fetchTags();
  }, [languages, params, config]);

  const meetingPlace = layoutData?.meetingPlacePageHome?.meetingPlace;
  const menuCards =
    layoutData?.meetingPlacePageHome?.menuCardsCollection?.items?.filter(
      Boolean,
    );
  const menuItem =
    layoutData?.meetingPlacePageHome?.menuItemCollection?.items?.filter(
      Boolean,
    );
  const searchResults = layoutData?.meetingPlacePageHome?.searchResults;
  const whatsappNumber = layoutData?.meetingPlacePageHome?.whatsappNumber;
  const chatbotSettings =
    layoutData?.meetingPlacePageHome?.meetingPlace?.ikeaChatbotSettings;
  const hideWhatsapp = pageContext?.hideWhatsapp;
  const isWebView = useGetMobileAppWebViewHeader();

  const { setUser, user } = useUser();
  const userRef = React.useRef(user);

  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [authGateReady, setAuthGateReady] = useState<boolean>(false);
  const [firstRunCleared, setFirstRunCleared] = useState<boolean>(false);

  const sendEventToDataLayer = (currentPath: string | null) => {
    if (isBrowser && dataLayer) {
      if (currentPath?.includes('profile')) {
        dataLayer.push({
          event: 'kiddyland_opened',
          user_status: isLivliFamily
            ? user
              ? 'logged_in'
              : 'guest'
            : undefined,
        });
      }
      if (currentPath?.includes('isHosted=true')) {
        dataLayer.push({
          event: 'booking_engine_opened',
          user_status: isLivliFamily
            ? user
              ? 'logged_in'
              : 'guest'
            : undefined,
        });
      }

      dataLayer.push({
        event: 'user_signIn',
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      });
    }
  };

  useEffect(() => {
    // LW-1743: Send event to GTM when user clicks on a link to IKEA
    const sendEventToGtm = (e: Event) => {
      const target = e.target as HTMLLinkElement;

      if (!isBrowser || !target) return;

      let href: string | null = null;

      if (target instanceof HTMLAnchorElement) {
        href = target.href;
      } else {
        const closestAnchor = target.closest(
          '.ikea',
        ) as HTMLAnchorElement | null;
        if (closestAnchor) href = closestAnchor.href;
      }

      if (href) {
        const matches = href.match(/(http|https):\/\/(www\.|)ikea\.(.*)/);
        if (matches && dataLayer) {
          dataLayer.push({
            event: 'click_to_ikea',
            user_status: isLivliFamily
              ? userRef.current
                ? 'logged_in'
                : 'guest'
              : undefined,
          });
        }
      }
    };
    const element = window.document.body;
    element.addEventListener('click', sendEventToGtm);
    return () => {
      element.removeEventListener('click', sendEventToGtm);
    };
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // First launch of the iOS/iPadOS home-screen PWA: clear any auth state that
  // carried over from Safari so the user starts logged out and can fresh login.
  // Gates the silent-login effect until the cleanup has finished.
  useEffect(() => {
    // A login callback (?code=) is in progress — let the callback effect handle
    // it and don't wipe the PKCE verifier mid-flow.
    if (searchParams.get('code') || searchParams.get('error')) {
      setAuthGateReady(true);
      return;
    }

    let cancelled = false;
    (async () => {
      const didCleanup = await clearIOSPWAFirstRunAuth();
      if (cancelled) return;
      if (didCleanup) {
        setUser(null);
        setFirstRunCleared(true);
      }
      setAuthGateReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newsletter = searchParams.get('newsletter');
    const authCode = searchParams.get('code');
    const status = searchParams.get('status');

    // Open newsletter modal
    if (newsletter === 'true') {
      const newsletterButton = document.getElementById('newsletter-button');
      newsletterButton?.click();
    }

    // Logout if profile deleted
    if (status === 'deleted') {
      setUser(null);
      logOutUser();
      clearUrlParams();
    }

    // Authenticate with code
    if ((loginEnabled || tempLoginEnabled) && authCode) {
      // Read the PKCE verifier stored before the B2C redirect
      const codeVerifier = sessionStorage.getItem('pkce_verifier');

      if (!codeVerifier) {
        console.error(
          '[Auth] Missing PKCE verifier — possible replay attack or stale redirect',
        );
        clearUrlParams();
        return;
      }
      // Clear verifier immediately — single use only
      sessionStorage.removeItem('pkce_verifier');
      sessionStorage.removeItem('silent_login');
      setShowSpinner(true);
      fetch('/api/auth/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: authCode, codeVerifier }),
      })
        .then(async (res) => {
          if (!res.ok) {
            throw new Error(`Callback failed: ${res.status}`);
          }
          return res.json();
        })
        .then(async (data: { ok: boolean }) => {
          if (!data?.ok) return;

          const user = await getProfileDetails(meetingPlace?.businessUnitCode);
          const isValidUser: boolean = checkIfValidUser(user);

          if (isValidUser) {
            setUser(user);

            // Restore original path the user was on before login
            const currentPath = localStorage.getItem('current_path');
            if (currentPath) {
              window.location.href = currentPath;
            }

            sendEventToDataLayer(currentPath);
          }
        })
        .catch((error) => {
          console.error('[Auth] Callback error:', error);
        })
        .finally(() => {
          setShowSpinner(false);
          localStorage.removeItem('current_path');
          clearUrlParams();
        });
    }
  }, [searchParams, loginEnabled, tempLoginEnabled]);

  useEffect(() => {
    if (!loginEnabled && !tempLoginEnabled) return;

    // Wait for the iOS PWA first-run cleanup before restoring any session.
    if (!authGateReady) return;

    // First-run PWA cleanup just logged the user out server-side. The SSR
    // `isLoggedInServer` prop is stale for this render, so ignore it and stay
    // logged out — the cleared cookie makes subsequent loads consistent.
    if (firstRunCleared) {
      setUser(null);
      return;
    }

    // Code in URL — Effect 1 is handling it, don't interfere
    if (searchParams.get('code') || searchParams.get('error')) return;

    // Silent login already in progress — don't trigger again
    if (sessionStorage.getItem('silent_login') === 'true') return;

    // check MFE login status
    const isLoggedIn = isLoggedInServer || Cookies.get('is_logged_in');
    if (!isLoggedIn) {
      setUser(null);
      return;
    }

    // Strip auth params from current path
    const filteredParams = new URLSearchParams(searchParams?.toString() || '');
    filteredParams.delete('code');
    filteredParams.delete('state');
    filteredParams.delete('session_state');
    filteredParams.delete('error');
    filteredParams.delete('error_description');
    const currentPath = filteredParams?.toString()
      ? `${pathname}?${filteredParams?.toString()}`
      : pathname;

    /**
     * /api/auth/refresh reads the httpOnly refresh_token cookie (invisible to JS),
     * checks if the id_token is still valid, and silently refreshes if needed.
     * Returns a valid id_token or 401 if the session has expired.
     */
    setShowSpinner(true);
    fetch('/api/auth/refresh', { method: 'POST' })
      .then(async (res) => {
        if (res.status === 401) {
          setShowSpinner(true);
          sessionStorage.setItem('silent_login', 'true');
          await initiateLogin(
            node_locale,
            meetingPlace?.businessUnitCode,
            config,
            currentPath,
          );
          return null;
        }
        if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
        return res.json();
      })
      .then(async (data: { ok: boolean } | null) => {
        if (!data?.ok) return;

        const user = await getProfileDetails(meetingPlace?.businessUnitCode);

        if (checkIfValidUser(user)) {
          setUser(user);
        } else {
          setUser(null);
        }
      })
      .catch((error) => {
        console.error('[Auth] Session restore error:', error);
        setUser(null);
        sessionStorage.removeItem('silent_login');
      })
      .finally(() => {
        setShowSpinner(false);
      });

    // Intentionally run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginEnabled, tempLoginEnabled, authGateReady, firstRunCleared]);

  useEffect(() => {
    applyGoogleTranslateDOMPatch();
  }, []);

  return (
    <CrowdLevelProvider
      businessUnitCode={meetingPlace?.businessUnitCode}
      timezone={meetingPlace?.timezone}
      showCrowdIndicator={config?.NEXT_PUBLIC_SHOW_CROWD_INDICATOR === 'true'}
    >
      {whatsappNumber && !hideWhatsapp && !isWebView && (
        <Whatsapp whatsappNumber={whatsappNumber} />
      )}
      {chatbotSettings?.enableChatbot &&
        chatbotSettings?.jsPath &&
        !isWebView && (
          <DigitalConcierge
            jsPath={chatbotSettings.jsPath}
            cssPath={chatbotSettings.cssPath}
          />
        )}
      <PreviewBanner isEnabled={preview} />
      {!shouldHideHeaderFooter && meetingPlace && !isWebView && (
        <TabBar
          menuGroups={menuItem}
          menuCards={menuCards}
          logo={meetingPlace?.logo}
          socialMediaLinks={meetingPlace?.socialMediaLinks}
          meetingPlace={meetingPlace}
          node_locale={node_locale}
          searchResults={searchResults}
          hrefLangTags={hrefLangTags}
          config={config}
        />
      )}
      {meetingPlace && !isWebView && isLivliFamily && (
        <PWABanner
          dictionary={dictionary}
          node_locale={meetingPlace?.sys?.locale}
          meetingPlaceName={meetingPlace?.name}
        />
      )}
      <div
        ref={childrenRef}
        className={`smartbanner-top-margin relative xl:container lg:px-28 xl:px-28 ${isWebView ? '!pb-6' : ''
          }`}
      >
        {!shouldHideHeaderFooter && meetingPlace && !isWebView && (
          <Header meetingPlace={meetingPlace} logo={meetingPlace?.logo} />
        )}
        {children}
      </div>
      {!shouldHideHeaderFooter && (
        <div>
          <Footer
            footerData={footerData}
            layoutData={layoutData}
            footerClass={undefined}
          />
        </div>
      )}
      <Spinner show={showSpinner} />
    </CrowdLevelProvider>
  );
};

export default Layout;
